import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useCompanyStore } from '@/stores/company'
import { useTemplatePreferences } from '@/composables/useTemplatePreferences'
import type {
  EmailTemplate,
  EmailTemplateCreatePayload,
  EmailTemplateUpdatePayload,
  TemplateVisibility,
} from '@/types/emailTemplate'

function getCompanyId(): string {
  try {
    const companyStore = useCompanyStore()
    if (companyStore.selectedCompanyId) return companyStore.selectedCompanyId
  } catch { /* store no inicializado aún */ }
  return localStorage.getItem('selectedCompanyId') || localStorage.getItem('emailCompanyId') || ''
}

function getCurrentUserId(): string {
  try {
    const auth = FirebaseProvider.getInstance().getAuth()
    return auth.currentUser?.uid || ''
  } catch {
    return ''
  }
}

export const useEmailTemplateStore = defineStore('emailTemplate', () => {
  const templates = ref<EmailTemplate[]>([])
  const currentTemplate = ref<EmailTemplate | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const activeTemplates = computed(() =>
    templates.value.filter((t) => t.isActive)
  )

  /**
   * Plantillas visibles para el usuario actual:
   * - Públicas activas
   * - Privadas activas creadas por el usuario actual
   */
  const { hiddenTemplateIds } = useTemplatePreferences()

  const visibleTemplates = computed(() => {
    const userId = getCurrentUserId()
    return templates.value.filter((t) => {
      if (!t.isActive) return false
      if (t.visibility === 'private') return t.createdBy === userId
      return !hiddenTemplateIds.value.includes(t.id)
    })
  })

  const templatesByModule = computed(() => (module: string) =>
    templates.value.filter((t) => t.modules.includes(module as any))
  )

  function getCollectionRef() {
    const companyId = getCompanyId()
    if (!companyId) throw new Error('No se encontró companyId')
    const db = FirebaseProvider.getInstance().getFirestore()
    return collection(db, `Companies/${companyId}/emailTemplates`)
  }

  async function fetchTemplates(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const colRef = getCollectionRef()
      const q = query(colRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      templates.value = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          name: data.name || '',
          description: data.description || '',
          subject: data.subject || '',
          bodyHtml: data.bodyHtml || '',
          modules: data.modules || [],
          dynamicFields: data.dynamicFields || [],
          category: data.category || '',
          visibility: (data.visibility as TemplateVisibility) || 'public',
          isActive: data.isActive ?? true,
          autoFillDateField: data.autoFillDateField || undefined,
          autoAttachDocumentTypes: data.autoAttachDocumentTypes || undefined,
          defaultRecipients: data.defaultRecipients || undefined,
          createdBy: data.createdBy || '',
          createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt || '',
          updatedBy: data.updatedBy || '',
          updatedAt: data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt || '',
          usageCount: data.usageCount || 0,
        } as EmailTemplate
      })
    } catch (err: any) {
      error.value = err.message || 'Error al cargar plantillas'
      console.error('Error fetching templates:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchTemplateById(id: string): Promise<EmailTemplate | null> {
    isLoading.value = true
    error.value = null

    try {
      const colRef = getCollectionRef()
      const docRef = doc(colRef, id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) return null

      const data = docSnap.data()
      const template: EmailTemplate = {
        id: docSnap.id,
        name: data.name || '',
        description: data.description || '',
        subject: data.subject || '',
        bodyHtml: data.bodyHtml || '',
        modules: data.modules || [],
        dynamicFields: data.dynamicFields || [],
        category: data.category || '',
        visibility: (data.visibility as TemplateVisibility) || 'public',
        isActive: data.isActive ?? true,
        autoFillDateField: data.autoFillDateField || undefined,
        autoAttachDocumentTypes: data.autoAttachDocumentTypes || undefined,
        defaultRecipients: data.defaultRecipients || undefined,
        createdBy: data.createdBy || '',
        createdAt: data.createdAt instanceof Timestamp
          ? data.createdAt.toDate().toISOString()
          : data.createdAt || '',
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt instanceof Timestamp
          ? data.updatedAt.toDate().toISOString()
          : data.updatedAt || '',
        usageCount: data.usageCount || 0,
      }

      currentTemplate.value = template
      return template
    } catch (err: any) {
      error.value = err.message || 'Error al cargar plantilla'
      console.error('Error fetching template:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function createTemplate(
    payload: EmailTemplateCreatePayload,
    userId: string
  ): Promise<string | null> {
    isLoading.value = true
    error.value = null

    try {
      const colRef = getCollectionRef()
      const now = Timestamp.now()

      const docData: any = {
        name: payload.name,
        description: payload.description || '',
        subject: payload.subject,
        bodyHtml: payload.bodyHtml,
        modules: payload.modules,
        dynamicFields: payload.dynamicFields,
        category: payload.category || '',
        visibility: payload.visibility || 'public',
        isActive: true,
        createdBy: userId,
        createdAt: now,
        updatedBy: userId,
        updatedAt: now,
        usageCount: 0,
      }

      if (payload.autoFillDateField) {
        docData.autoFillDateField = payload.autoFillDateField
      }

      if (payload.autoAttachDocumentTypes && payload.autoAttachDocumentTypes.length > 0) {
        docData.autoAttachDocumentTypes = payload.autoAttachDocumentTypes
      }

      if (payload.defaultRecipients) {
        docData.defaultRecipients = payload.defaultRecipients
      }

      const docRef = await addDoc(colRef, docData)

      // Agregar al estado local
      const newTemplate: EmailTemplate = {
        id: docRef.id,
        ...docData,
        createdAt: now.toDate().toISOString(),
        updatedAt: now.toDate().toISOString(),
      }
      templates.value.unshift(newTemplate)

      return docRef.id
    } catch (err: any) {
      error.value = err.message || 'Error al crear plantilla'
      console.error('Error creating template:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function updateTemplate(
    id: string,
    payload: EmailTemplateUpdatePayload,
    userId: string
  ): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const colRef = getCollectionRef()
      const docRef = doc(colRef, id)

      const updateData: Record<string, any> = {
        updatedBy: userId,
        updatedAt: Timestamp.now(),
      }

      if (payload.name !== undefined) updateData.name = payload.name
      if (payload.description !== undefined) updateData.description = payload.description
      if (payload.subject !== undefined) updateData.subject = payload.subject
      if (payload.bodyHtml !== undefined) updateData.bodyHtml = payload.bodyHtml
      if (payload.modules !== undefined) updateData.modules = payload.modules
      if (payload.dynamicFields !== undefined) updateData.dynamicFields = payload.dynamicFields
      if (payload.category !== undefined) updateData.category = payload.category
      if (payload.isActive !== undefined) updateData.isActive = payload.isActive
      if (payload.visibility !== undefined) updateData.visibility = payload.visibility
      if (payload.autoFillDateField !== undefined) {
        updateData.autoFillDateField = payload.autoFillDateField === null ? deleteField() : payload.autoFillDateField
      }

      if (payload.autoAttachDocumentTypes !== undefined) {
        updateData.autoAttachDocumentTypes = payload.autoAttachDocumentTypes
      }

      if (payload.defaultRecipients !== undefined) {
        updateData.defaultRecipients = payload.defaultRecipients
      }

      await updateDoc(docRef, updateData)

      // Actualizar estado local
      const idx = templates.value.findIndex((t) => t.id === id)
      if (idx !== -1) {
        const updated: EmailTemplate = {
          ...templates.value[idx],
          ...payload,
          autoFillDateField: payload.autoFillDateField === null ? undefined : (payload.autoFillDateField || templates.value[idx].autoFillDateField),
          updatedBy: userId,
          updatedAt: new Date().toISOString(),
        }
        templates.value[idx] = updated
      }

      if (currentTemplate.value?.id === id) {
        currentTemplate.value = {
          ...currentTemplate.value,
          ...payload,
          autoFillDateField: payload.autoFillDateField === null ? undefined : (payload.autoFillDateField || currentTemplate.value.autoFillDateField),
          updatedBy: userId,
          updatedAt: new Date().toISOString(),
        }
      }

      return true
    } catch (err: any) {
      error.value = err.message || 'Error al actualizar plantilla'
      console.error('Error updating template:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function deleteTemplate(id: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const colRef = getCollectionRef()
      const docRef = doc(colRef, id)
      await deleteDoc(docRef)

      templates.value = templates.value.filter((t) => t.id !== id)

      if (currentTemplate.value?.id === id) {
        currentTemplate.value = null
      }

      return true
    } catch (err: any) {
      error.value = err.message || 'Error al eliminar plantilla'
      console.error('Error deleting template:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function duplicateTemplate(
    id: string,
    userId: string
  ): Promise<string | null> {
    const original = templates.value.find((t) => t.id === id)
    if (!original) return null

    return createTemplate(
      {
        name: `${original.name} (copia)`,
        description: original.description,
        subject: original.subject,
        bodyHtml: original.bodyHtml,
        modules: [...original.modules],
        dynamicFields: [...original.dynamicFields],
        category: original.category,
        autoAttachDocumentTypes: original.autoAttachDocumentTypes,
        defaultRecipients: original.defaultRecipients,
      },
      userId
    )
  }

  async function incrementUsageCount(id: string): Promise<void> {
    try {
      const colRef = getCollectionRef()
      const docRef = doc(colRef, id)
      const tpl = templates.value.find((t) => t.id === id)
      if (tpl) {
        const newCount = (tpl.usageCount || 0) + 1
        await updateDoc(docRef, { usageCount: newCount })
        tpl.usageCount = newCount
      }
    } catch (err) {
      console.error('Error incrementing usage count:', err)
    }
  }

  return {
    // State
    templates,
    currentTemplate,
    isLoading,
    error,
    // Getters
    activeTemplates,
    visibleTemplates,
    templatesByModule,
    // Actions
    fetchTemplates,
    fetchTemplateById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    incrementUsageCount,
  }
})
